import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import _ from "lodash";
import { Observable, Subject, forkJoin, of } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { SkogsvardsStatus } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { AnsvarigAnvandareModel, IVKSearchFilter } from "~app/features/virkeskop/anbud-kontrakt/models/kontrakt-search-filter-model";
import { AnbudKontraktSearchService } from "~app/features/virkeskop/anbud-kontrakt/services/anbud-kontrakt-search.service";
import { IAtgardstypWithGroup } from "~app/features/virkeskop/models/atgardstyp-with-group-dto";
import { ATGARD_PLANERINGSSTATUS, KONTRAKTSTATUSARFPV } from "~app/features/virkeskop/models/kontrakt-detail-model";
import { ISearchVirkeskopAnbudKontraktRequest } from "~app/features/virkeskop/models/kontrakt-search-model";
import { SkogsvardService } from "~app/features/virkeskop/services/skogsvard.service";
import { VirkeskopService } from "~app/features/virkeskop/services/virkeskop.service";
import { VirkesleverantorService } from "~app/features/virkeskop/virkesleverantor/services/virkesleverantor.service";
import { KontraktSaveFilterComponent } from "../../../virkeskop/anbud-kontrakt/sok-anbud-kontrakt/kontrakt-search-mina-filter/kontrakt-save-filter/kontrakt-save-filter.component";
import { KontraktSearchMinaFilterComponent } from "../../../virkeskop/anbud-kontrakt/sok-anbud-kontrakt/kontrakt-search-mina-filter/kontrakt-search-mina-filter.component";

// tree-selector tycks inte fungera så bra ihop med form-binder, så åtgärdstyp lagras utanför modellen
type FormModel = Omit<IVKSearchFilter, "atgardstyp" | "skogsvardstyp"> & FpvSearchModel;

type FpvSearchModel = {
    ansvarigSaljare: AnsvarigAnvandareModel[];
    kund: KundSearchModel[];
    kontaktperson: any[];
};

type KundSearchModel = {
    id: number;
    namn: string;
    kod?: string;
};

const buildAtgardstypTree = (atgardstyper: IAtgardstypWithGroup[]): SelectionItem[] => {
    if (!atgardstyper) {
        return [];
    }

    const mapAtgardstypToSelectionItem = ({ namn, kortnamn }: IAtgardstypWithGroup): SelectionItem => ({
        text: namn,
        value: kortnamn
    });

    const groups = _.chain(atgardstyper)
        .map("grupp")
        .uniq()
        .sort().value();
    const tree: SelectionItem[] = groups.map((grupp) => {
        let children = atgardstyper.filter((x) => x.grupp === grupp);
        children = _.sortBy(children, "namn");

        // i det fall att gruppen bara innehåller en typ står den bland grupperna
        if (children.length === 1) {
            const [atgTyp] = children;
            return mapAtgardstypToSelectionItem(atgTyp);
        }

        const childItems = children.map(mapAtgardstypToSelectionItem);
        return {
            text: grupp,
            value: grupp,
            children: childItems
        };
    });

    return tree;
};

const DEFAULT_MODEL: FormModel = {
    ansvarigVirkeskopare: [],
    ansvarigSaljare: [],
    ansvarigFaltplanerare: [],
    virkesleverantor: [],
    arbetsorderbeteckning: "",
    sok: "",
    affarsform: [],
    anskaffningsform: "",
    kontraktstyp: undefined,
    kontraktstatus: [],
    planeringsstatus: [],
    vacsStatus: [],
    skogsvardsStatus: [],
    sluttransporterat: false,
    kund: [],
    kontaktperson: []
};

@Component({
    selector: "rask-sok-skogsvard-fpv-avtal",
    templateUrl: "./sok-skogsvard-fpv-avtal.component.html",
    styleUrls: ["./sok-skogsvard-fpv-avtal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        KontraktSearchMinaFilterComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        TreeSelectorComponent,
        InputComponent,
        SubmitButtonComponent,
        ButtonComponent,
        KontraktSaveFilterComponent
    ]
})
export class SokFPVAvtalComponent implements OnInit, OnDestroy {
    protected atgardPlaneringstatusar: SelectionItem[] = ATGARD_PLANERINGSSTATUS.map((value) => ({
        text: value,
        value
    }));
    protected form: UntypedFormGroup;
    protected kontraktstatusar: SelectionItem[] = KONTRAKTSTATUSARFPV.map((value) => ({
        text: value,
        value
    }));
    protected model = _.cloneDeep(DEFAULT_MODEL);
    protected skogsvardsItems = [] as SelectionItem[];
    protected skogsvardsStatusItems: Observable<SelectionItem[]> = of(SkogsvardsStatus.alla.map(x => ({
        value: x,
        text: x,
        selected: false
    })));
    protected skogsvardsStautsSelection: SelectionItem[];

    private readonly anbudKontraktSearchService = inject(AnbudKontraktSearchService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly unsubscribe$ = new Subject<void>();
    private readonly virkeskopService = inject(VirkeskopService);
    private readonly virkesleverantorService = inject(VirkesleverantorService);

    private get skogsvardstyp(): string[] {
        return _.chain(this.skogsvardsItems)
            .map((x) => x.children ?? x)
            .flatten()
            .filter((x) => !x.children && x.selected)
            .map((x) => x.value)
            .value();
    }

    ngOnDestroy(): void {
        this.anbudKontraktSearchService.saveRecentScrollPosition();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.form = this.formBinderService.CreateGroup(this.model);

        this.form.valueChanges.subscribe(x => {
            this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
        });

        this.anbudKontraktSearchService.searchFilterTemplate$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(filter => {
                this.applyFilter(filter, false);
            });
        this.initSearchFilter();
    }

    protected atgardsTyperChanged() {
        this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
    }

    protected onClear() {
        _.assign(this.model, _.cloneDeep(DEFAULT_MODEL));
        this.clearAtgardstyp(this.skogsvardsItems);
        this.skogsvardsStautsSelection = [];
        this.modelChanged();
    }

    protected onSearch() {
        const request = this.getRequestFromForm();
        const observable = this.virkeskopService.searchSkogsvardFPVAvtal(request);
        this.anbudKontraktSearchService.search(observable);
    }

    protected onSkogsvardsStatusChanged(items: SelectionItem[]) {
        this.skogsvardsStautsSelection = items;
        this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
    }

    protected getAnvandareTypeAhead = (input: string) => this.virkesleverantorService.getAnvandareTypeAhead(input);

    protected searchKundTypeAhead = (input: string) => {
        if (input.length < 3) {
            return of([]);
        }
        return this.skogsvardService.searchKundTypeAhead(input);
    };

    protected searchForKontaktpersonTypeAhead = (input: string) => {
        const selectedKund = this.form.value.kund?.[0];
        if (!selectedKund?.id || input.length < 1) {
            return of([]);
        }

        return this.skogsvardService.getKontaktpersonerForKund(selectedKund.id).pipe(
            map(kontaktpersoner =>
                kontaktpersoner.filter(k => {
                    const fullName = `${k.fornamn} ${k.efternamn}`.toLowerCase();
                    return fullName.includes(input.toLowerCase());
                })
            )
        );
    };

    protected kundViewFormatter = (kund: any): string =>
        kund?.kod ? `${kund.namn} (${kund.kod})` : kund?.namn || "";

    protected kontaktpersonViewFormatter = (kontaktperson: any): string =>
        kontaktperson ? `${kontaktperson.fornamn} ${kontaktperson.efternamn}` : "";

    protected skogsvardViewFormatterFn = (item: SelectionItem): string => item ? `${item.text}` : "";

    private applyDefaultSearchFilter() {
        const defaultAnsvarig = this.authenticationService.getUserData();

        this.model.ansvarigVirkeskopare = [
            { kod: defaultAnsvarig.userName,
                namn: defaultAnsvarig.fullUserName }
        ];
        this.modelChanged();
        this.onSearch();
    }

    private applyFilter(filter: IVKSearchFilter, performSearch: boolean) {
        // Gör så att filtret får defaultvärden från defaultmodellen först, annars så rensas inte nya värden
        const filterToApply = {} as IVKSearchFilter;
        _.assign(filterToApply, _.cloneDeep(DEFAULT_MODEL));
        filterToApply.atgardstyp = [];
        filterToApply.skogsvardstyp = [];
        _.assign(filterToApply, _.cloneDeep(filter));

        // Hanterar de fall där det är ett gammalt filter från när det endast gick att söka på en ansvarig virkesköpare
        const ansvarigVkOld = (filterToApply as any)?.ansvarigVirkeskopare;
        if (filterToApply && ObjectUtils.hasValue(ansvarigVkOld) && !Array.isArray(ansvarigVkOld)) {
            filterToApply.ansvarigVirkeskopare = [ansvarigVkOld];
        }
        if (filterToApply && ObjectUtils.hasValue((filterToApply as any).ansvarigvirkeskopareNamn)) {
            delete (filterToApply as any).ansvarigvirkeskopare;
            delete (filterToApply as any).ansvarigvirkeskopareNamn;
            delete (filterToApply as any).sokMinaPagaende;
        }
        _.assign(this.model, filterToApply);
        this.applyFilterToAtgardsTyp(filterToApply.skogsvardstyp, this.skogsvardsItems);
        this.applyFilterToSkogsvardsStatus(filterToApply.skogsvardsStatus);
        this.modelChanged();

        if (performSearch) {
            this.onSearch();
        }
    }

    private applyFilterToAtgardsTyp(filterAtgardsTyper: string [], typItems: SelectionItem[]) {
        typItems.forEach(grupp => {
            grupp.children?.forEach(typ => {
                typ.selected = filterAtgardsTyper.includes(typ.value);
            });
            if (!grupp.children) {
                // Specialhantering av Grot och Leveransvirke (som inte har några children i detta sammanhang)
                grupp.selected = filterAtgardsTyper.includes(grupp.value);
            } else {
                grupp.selected = grupp.children.every(x => x.selected);
                grupp.partialSelected = (!grupp.selected && grupp.children.some(x => x.selected));
            }
        });
    }

    private applyFilterToSkogsvardsStatus(filterAtgardsTyper: string []) {
        this.skogsvardsStautsSelection = filterAtgardsTyper.map(x => ({
            value: x,
            text: x,
            selected: true
        }));
    }

    private clearAtgardstyp(typItems: SelectionItem[]) {
        const items = _.chain(typItems)
            .map((x) => [
                x,
                ...(x.children ?? [])
            ])
            .flatten()
            .value();

        items.forEach((x) => x.selected = false);
    }

    private getFilterFromForm(): IVKSearchFilter {
        return {
            ...this.model,
            atgardstyp: [],
            skogsvardstyp: this.skogsvardstyp,
            skogsvardsStatus: this.skogsvardsStautsSelection?.map(x => x.text)
        };
    }

    private getRequestFromForm(): ISearchVirkeskopAnbudKontraktRequest {
        const { model, skogsvardstyp } = this;
        const avk = model.ansvarigVirkeskopare?.map(x => x.kod) ?? [];
        const as = model.ansvarigSaljare.map(x => x.kod);
        const kund = model.kund?.map(x => x.id) ?? [];
        const kontaktperson = model.kontaktperson?.map(x => x.id) ?? [];
        return {
            ...model,
            atgardstyp: [],
            skogsvardstyp,
            skogsvardsStatus: this.skogsvardsStautsSelection?.map(x => x.text) ?? [],
            kontraktstyp: model.kontraktstyp ?? "",
            anskaffningsform: model.anskaffningsform ?? "",
            ansvarigVirkeskopare: avk,
            ansvarigSaljare: as,
            ansvarigFaltplanerare: [],
            sluttransporterat: model.sluttransporterat ?? false,
            kund: kund,
            kontaktperson: kontaktperson,
            virkesleverantor: []
        };
    }

    private getSkogsvardstyper() {
        return this.skogsvardService.getSkogsvardsTyperWithGroups();
    }

    private initSearchFilter() {
        forkJoin([
            this.getSkogsvardstyper(),
            this.anbudKontraktSearchService.getRecentFilter()
        ]).subscribe(([
            skogsvardstyperDto,
            filter
        ]) => {
            this.skogsvardsItems = buildAtgardstypTree(skogsvardstyperDto);
            if (filter) {
                this.applyFilter(filter, true);
            } else {
                this.applyDefaultSearchFilter();
            }
        });
    }

    private modelChanged() {
        const model = _.cloneDeep(this.model);
        this.formBinderService.updateFromModel(this.form, model, true);
    }
}
